import React, { createContext, useContext, useState, ReactNode } from 'react';

export type ThemeType = 'tiki' | 'pirate' | 'british' | 'classic' | 'tropical' | 'speakeasy';

export interface ThemeColors {
  primary: string;
  secondary: string;
  accent: string;
  background: string;
  surface: string;
  surfaceVariant: string;
  surfaceHighlight: string;
  cardBackground: string;
  cardBackgroundVariant: string;
  cardBorder: string;
  text: string;
  textSecondary: string;
  textTertiary: string;
  border: string;
  success: string;
  warning: string;
  error: string;
  // Additional fun colors for each theme
  highlight1: string;
  highlight2: string;
  highlight3: string;
  highlight4: string;
}

export interface ThemeData {
  name: string;
  description: string;
  colors: ThemeColors;
  icon: string;
  backgroundPattern?: string;
}

const themes: Record<ThemeType, ThemeData> = {
  tiki: {
    name: 'Tiki Paradise',
    description: 'Tropical vibes with bamboo and palm fronds',
    icon: '🌺',
    colors: {
      primary: '#FF6B35', // Tiki orange
      secondary: '#4ECDC4', // Tropical teal
      accent: '#FFE66D', // Pineapple yellow
      background: '#1A1A2E', // Dark navy
      surface: '#16213E', // Darker navy
      surfaceVariant: '#0F3460', // Even darker navy
      surfaceHighlight: '#533483', // Purple highlight
      cardBackground: '#2A2A4A', // Card background
      cardBackgroundVariant: '#3A3A5A', // Card variant
      cardBorder: '#FF6B35', // Orange border
      text: '#FFFFFF',
      textSecondary: '#B8B8B8',
      textTertiary: '#8A8A8A',
      border: '#FF6B35',
      success: '#4ECDC4',
      warning: '#FFE66D',
      error: '#FF6B6B',
      highlight1: '#FF8E53', // Light orange
      highlight2: '#7FDBDA', // Light teal
      highlight3: '#FFD93D', // Bright yellow
      highlight4: '#FF6B9D' // Pink
    },
    backgroundPattern: 'bamboo'
  },
  pirate: {
    name: 'Pirate Cove',
    description: 'Swashbuckling adventure with rum and treasure',
    icon: '🏴‍☠️',
    colors: {
      primary: '#8B4513', // Dark brown
      secondary: '#DAA520', // Goldenrod
      accent: '#CD853F', // Peru brown
      background: '#2F1B14', // Dark brown
      surface: '#3D2817', // Lighter brown
      surfaceVariant: '#4A2E1A', // Even lighter brown
      surfaceHighlight: '#8B4513', // Brown highlight
      cardBackground: '#5A3A2A', // Card background
      cardBackgroundVariant: '#6A4A3A', // Card variant
      cardBorder: '#DAA520', // Gold border
      text: '#F5DEB3', // Wheat
      textSecondary: '#DEB887',
      textTertiary: '#CD853F',
      border: '#DAA520',
      success: '#32CD32',
      warning: '#FFD700',
      error: '#DC143C',
      highlight1: '#FFD700', // Bright gold
      highlight2: '#CD853F', // Peru
      highlight3: '#8B7355', // Tan
      highlight4: '#B8860B' // Dark goldenrod
    },
    backgroundPattern: 'wood'
  },
  british: {
    name: 'British Gentleman',
    description: 'Sophisticated gin and afternoon tea',
    icon: '🇬🇧',
    colors: {
      primary: '#1E3A8A', // Royal blue
      secondary: '#DC2626', // British red
      accent: '#F59E0B', // Gold
      background: '#F8FAFC', // Light gray
      surface: '#FFFFFF',
      surfaceVariant: '#F1F5F9', // Slightly darker
      surfaceHighlight: '#E2E8F0', // Highlight
      cardBackground: '#FFFFFF', // White cards
      cardBackgroundVariant: '#F8FAFC', // Light variant
      cardBorder: '#1E3A8A', // Blue border
      text: '#1F2937',
      textSecondary: '#6B7280',
      textTertiary: '#9CA3AF',
      border: '#E5E7EB',
      success: '#059669',
      warning: '#D97706',
      error: '#DC2626',
      highlight1: '#3B82F6', // Blue
      highlight2: '#EF4444', // Red
      highlight3: '#F59E0B', // Amber
      highlight4: '#10B981' // Emerald
    },
    backgroundPattern: 'pinstripe'
  },
  classic: {
    name: 'Classic Cocktail',
    description: 'Timeless elegance and sophistication',
    icon: '🍸',
    colors: {
      primary: '#1F2937', // Dark gray
      secondary: '#6B7280', // Medium gray
      accent: '#F59E0B', // Gold
      background: '#FFFFFF',
      surface: '#F9FAFB',
      surfaceVariant: '#F3F4F6', // Slightly darker
      surfaceHighlight: '#E5E7EB', // Highlight
      cardBackground: '#FFFFFF', // White cards
      cardBackgroundVariant: '#F9FAFB', // Light variant
      cardBorder: '#1F2937', // Dark border
      text: '#111827',
      textSecondary: '#6B7280',
      textTertiary: '#9CA3AF',
      border: '#D1D5DB',
      success: '#059669',
      warning: '#D97706',
      error: '#DC2626',
      highlight1: '#374151', // Gray
      highlight2: '#F59E0B', // Amber
      highlight3: '#6B7280', // Gray
      highlight4: '#D1D5DB' // Light gray
    },
    backgroundPattern: 'elegant'
  },
  tropical: {
    name: 'Tropical Sunset',
    description: 'Vibrant colors and island vibes',
    icon: '🌅',
    colors: {
      primary: '#FF6B6B', // Coral
      secondary: '#4ECDC4', // Turquoise
      accent: '#FFE66D', // Yellow
      background: '#2C3E50', // Dark blue-gray
      surface: '#34495E', // Lighter blue-gray
      surfaceVariant: '#2C3E50', // Darker variant
      surfaceHighlight: '#8E44AD', // Purple highlight
      cardBackground: '#4A5568', // Card background
      cardBackgroundVariant: '#5A6A7A', // Card variant
      cardBorder: '#FF6B6B', // Coral border
      text: '#FFFFFF',
      textSecondary: '#BDC3C7',
      textTertiary: '#95A5A6',
      border: '#FF6B6B',
      success: '#2ECC71',
      warning: '#F39C12',
      error: '#E74C3C',
      highlight1: '#FF8E8E', // Light coral
      highlight2: '#7FDBDA', // Light turquoise
      highlight3: '#FFD93D', // Bright yellow
      highlight4: '#FF6B9D' // Pink
    },
    backgroundPattern: 'palm'
  },
  speakeasy: {
    name: 'Speakeasy',
    description: '1920s prohibition era glamour',
    icon: '🎭',
    colors: {
      primary: '#8B0000', // Dark red
      secondary: '#DAA520', // Gold
      accent: '#2F4F4F', // Dark slate
      background: '#1A1A1A', // Very dark
      surface: '#2D2D2D', // Dark gray
      surfaceVariant: '#1A1A1A', // Darker variant
      surfaceHighlight: '#4A4A4A', // Highlight
      cardBackground: '#3A3A3A', // Card background
      cardBackgroundVariant: '#4A4A4A', // Card variant
      cardBorder: '#DAA520', // Gold border
      text: '#F5F5DC', // Beige
      textSecondary: '#D2B48C',
      textTertiary: '#A0522D',
      border: '#DAA520',
      success: '#228B22',
      warning: '#FFD700',
      error: '#DC143C',
      highlight1: '#B22222', // Fire brick
      highlight2: '#FFD700', // Gold
      highlight3: '#696969', // Dim gray
      highlight4: '#CD853F' // Peru
    },
    backgroundPattern: 'art-deco'
  }
};

interface ThemeContextType {
  currentTheme: ThemeType;
  themeData: ThemeData;
  setTheme: (theme: ThemeType) => void;
  availableThemes: ThemeType[];
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

interface ThemeProviderProps {
  children: ReactNode;
}

export const ThemeProvider: React.FC<ThemeProviderProps> = ({ children }) => {
  const [currentTheme, setCurrentTheme] = useState<ThemeType>('tiki');

  const setTheme = (theme: ThemeType) => {
    setCurrentTheme(theme);
    // Save to localStorage for persistence
    localStorage.setItem('cocktail-theme', theme);
  };

  // Load theme from localStorage on mount
  React.useEffect(() => {
    const savedTheme = localStorage.getItem('cocktail-theme') as ThemeType;
    if (savedTheme && themes[savedTheme]) {
      setCurrentTheme(savedTheme);
    }
  }, []);

  const value: ThemeContextType = {
    currentTheme,
    themeData: themes[currentTheme],
    setTheme,
    availableThemes: Object.keys(themes) as ThemeType[]
  };

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
};
